import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging } from '~/infrastructure/paging';
import {
  MaintIntervalTypeLookupDto,
  MaintenanceAssetScheduleDto,
  MaintenanceAssetScheduleRequestModel,
  MaintenanceIntervalDto,
  MaintenanceTypeDto,
  MaintenanceTypeRequestModel
} from '~/models/modules/Asset/Transactions/Maintenance/maintenance.model';
import { ApiServiceBase, API_NAMES } from '~/services/api';

@Injectable({ providedIn: 'root' })
export class ScheduleMaintenanceService extends ApiServiceBase {
  constructor(injector: Injector, private http: HttpClient) {
    super(injector);
    this.setBase(API_NAMES.assets);
  }

  getGridData(paramsObj?: any, model?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http
      .post<MaintenanceAssetScheduleDto[]>(this.url('/ScheduleMaintenance/grid-data'), model, {
        params,
        observe: 'response',
      })
      .pipe(map(augmentPaging));
  }

  getAllSchedules(filters?: {
    assetId?: number;
    personId?: number;
    siteId?: number;
    isPerformed?: boolean;
    isRecurring?: boolean;
    dueDateFrom?: string | Date;
    dueDateTo?: string | Date;
  }): Observable<MaintenanceAssetScheduleDto[]> {
    let params = new HttpParams();
    if (filters) {
      if (filters.assetId != null) params = params.set('assetId', filters.assetId.toString());
      if (filters.personId != null) params = params.set('personId', filters.personId.toString());
      if (filters.siteId != null) params = params.set('siteId', filters.siteId.toString());
      if (filters.isPerformed != null) params = params.set('isPerformed', filters.isPerformed.toString());
      if (filters.isRecurring != null) params = params.set('isRecurring', filters.isRecurring.toString());
      if (filters.dueDateFrom) {
        const fromStr = filters.dueDateFrom instanceof Date ? filters.dueDateFrom.toISOString() : filters.dueDateFrom;
        params = params.set('dueDateFrom', fromStr);
      }
      if (filters.dueDateTo) {
        const toStr = filters.dueDateTo instanceof Date ? filters.dueDateTo.toISOString() : filters.dueDateTo;
        params = params.set('dueDateTo', toStr);
      }
    }
    return this.http.get<MaintenanceAssetScheduleDto[]>(this.url('/ScheduleMaintenance'), { params });
  }

  getScheduleById(id: number): Observable<MaintenanceAssetScheduleDto> {
    return this.http.get<MaintenanceAssetScheduleDto>(this.url(`/ScheduleMaintenance/${id}`));
  }

  createSchedule(model: MaintenanceAssetScheduleRequestModel): Observable<MaintenanceAssetScheduleDto> {
    return this.http.post<MaintenanceAssetScheduleDto>(this.url('/ScheduleMaintenance'), model);
  }

  updateSchedule(id: number, model: MaintenanceAssetScheduleRequestModel): Observable<MaintenanceAssetScheduleDto> {
    return this.http.put<MaintenanceAssetScheduleDto>(this.url(`/ScheduleMaintenance/${id}`), model);
  }

  deleteSchedule(id: number): Observable<any> {
    return this.http.delete(this.url(`/ScheduleMaintenance/${id}`));
  }

  batchActivateDeactivate(obj: any): Observable<any> {
    return this.http.put(this.url('/ScheduleMaintenance/batchActivateDeactivate'), obj);
  }

  reactivateSchedule(id: number): Observable<any> {
    return this.http.put(this.url(`/ScheduleMaintenance/reactivate/${id}`), {});
  }

  deactivateSchedule(id: number): Observable<any> {
    return this.http.put(this.url(`/ScheduleMaintenance/deactivate/${id}`), {});
  }

  getMaintenanceTypes(siteId?: number, search?: string): Observable<MaintenanceTypeDto[]> {
    let params = new HttpParams();
    if (siteId != null) params = params.set('siteId', siteId.toString());
    if (search) params = params.set('search', search);
    return this.http.get<MaintenanceTypeDto[]>(this.url('/ScheduleMaintenance/types'), { params });
  }

  createMaintenanceType(model: MaintenanceTypeRequestModel): Observable<MaintenanceTypeDto> {
    return this.http.post<MaintenanceTypeDto>(this.url('/ScheduleMaintenance/types'), model);
  }

  updateMaintenanceType(id: number, model: MaintenanceTypeRequestModel): Observable<MaintenanceTypeDto> {
    return this.http.put<MaintenanceTypeDto>(this.url(`/ScheduleMaintenance/types/${id}`), model);
  }

  deleteMaintenanceType(id: number): Observable<any> {
    return this.http.delete(this.url(`/ScheduleMaintenance/types/${id}`));
  }

  getMaintenanceIntervals(): Observable<MaintenanceIntervalDto[]> {
    return this.http.get<MaintenanceIntervalDto[]>(this.url('/ScheduleMaintenance/intervals'));
  }

  getMaintIntervalTypes(): Observable<MaintIntervalTypeLookupDto[]> {
    return this.http.get<MaintIntervalTypeLookupDto[]>(this.url('/ScheduleMaintenance/interval-types'));
  }
}
